import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Form, Badge, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { Code, RefreshCw, Trash2, Plus, Search, Edit2 } from 'lucide-react';
import { PageTransition, useModal } from '../../../components/layout';
import { useAuth } from '../../../contexts/AuthContext';
import { useProfileManagement } from '../../../components/auth/useProfileManagement';
import { API_ENDPOINTS } from '../../../config/api.config';
import { Alert } from 'react-bootstrap';
import {ApiSource} from '../../../types';
import { getConfigValue } from "../../../utils/apiSourceUtils";
import { apiService } from '../../../services/apiService';
import DeleteConfirmationModal from '../../../components/modals/common/DeleteConfirmationModal';
import ContentSkeleton from "@/components/loading/ContentSkeleton.tsx";

interface ApiSourceUsageResponse {
    knowledgeBases: Array<{ id: string; name: string }>;
}

interface ApiSourceResponse {
    apiSources: ApiSource[];
    total: number;
    page: number;
    totalPages: number;
    limit: number;
}

interface PaginationData {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
}

const ITEMS_PER_PAGE = 10;

function toDisplayString(value: unknown): string {
    if (value === null || value === undefined) return '';
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
}

const APISourcesIndex: React.FC = () => {
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [sourceToDelete, setSourceToDelete] = useState<ApiSource | null>(null);
    const navigate = useNavigate();
    const { user } = useAuth();
    const modalState = useModal();
    const [sources, setSources] = useState<ApiSource[]>([]);
    const [selectedSource, setSelectedSource] = useState<ApiSource | null>(null);
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [pagination, setPagination] = useState<PaginationData>({
        page: 1,
        limit: ITEMS_PER_PAGE,
        total: 0,
        totalPages: 0
    });
    useProfileManagement(
        undefined,
        modalState.setError,
        modalState.setSuccess,
        modalState.setIsLoading,
        modalState.setShowProfileModal,
        modalState.setShowPasswordModal
    );
    const fetchSources = async (page: number, search?: string) => {
        try {
            setLoading(true);
            const queryParams = new URLSearchParams({
                page: page.toString(),
                limit: ITEMS_PER_PAGE.toString(),
                ...(search ? { search } : {})
            });

            const response = await apiService.get<ApiSourceResponse>(
                `${API_ENDPOINTS.DATA_SOURCE.API_SOURCES.LIST}?${queryParams}`
            );

            // Fetch usage data for each source
            const sourcesWithUsage = await Promise.all(
                (response.apiSources || []).map(async (source: ApiSource) => {
                    try {
                        const usageData = await apiService.get<ApiSourceUsageResponse>(
                            API_ENDPOINTS.DATA_SOURCE.API_SOURCES.USAGE.replace(':id', source.id)
                        );
                        return {
                            ...source,
                            used_by_kbs: usageData.knowledgeBases
                        };
                    } catch (error) {
                        console.error(`Failed to fetch usage for source ${source.id}:`, error);
                        return source;
                    }
                })
            );

            setSources(sourcesWithUsage);
            setCurrentPage(response.page);
            setPagination({
                page: response.page,
                limit: response.limit,
                total: response.total,
                totalPages: response.totalPages
            });
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to fetch API sources');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchSources(1);
    }, []);

    const handlePageChange = (newPage: number) => {
        setCurrentPage(newPage);
        fetchSources(newPage, searchTerm);
    };

    const handleSearch = (value: string) => {
        setSearchTerm(value);
        setCurrentPage(1);
        fetchSources(1, value);
    };

    const handleDeleteSource = async (source: ApiSource) => {
        setSourceToDelete(source);
        setShowDeleteModal(true);
    };

    const confirmDelete = async () => {
        if (!sourceToDelete) return;

        try {
            await apiService.delete(
                API_ENDPOINTS.DATA_SOURCE.API_SOURCES.DELETE.replace(':id', sourceToDelete.id)
            );
            await fetchSources(currentPage, searchTerm);
            setSelectedSource(null);
            modalState.setSuccess('API source deleted successfully');
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to delete API source');
        } finally {
            setShowDeleteModal(false);
            setSourceToDelete(null);
        }
    };

    const handleTestSource = async (sourceId: string) => {
        try {
            await apiService.post(
                API_ENDPOINTS.DATA_SOURCE.API_SOURCES.TEST.replace(':id', sourceId),
                {}
            );
            modalState.setSuccess('API source test successful');
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to test API source');
        }
    };

    const getRequestsPerMinute = (source: ApiSource): string => {
        const rpm = getConfigValue(source, 'requests_per_minute');
        return toDisplayString(rpm);
    };

    const getMethodAndEndpoint = (source: ApiSource) => {
        const method = toDisplayString(getConfigValue(source, 'method') || 'GET');
        const endpoint = toDisplayString(getConfigValue(source, 'endpoint') || '');

        return (
            <span>
                <Badge
                    className="me-1"
                    bg="light"
                    text="primary"
                    style={{
                        fontSize: '11px',
                        padding: '2px 6px',
                        fontWeight: '600'
                    }}
                >
                    {method}
                </Badge>
                <span className="text-muted">{endpoint}</span>
            </span>
        );
    };

    const renderPagination = () => {
        const maxVisiblePages = 5;
        let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
        let endPage = Math.min(pagination.totalPages, startPage + maxVisiblePages - 1);

        if (endPage - startPage + 1 < maxVisiblePages) {
            startPage = Math.max(1, endPage - maxVisiblePages + 1);
        }

        const pages = [];
        for (let i = startPage; i <= endPage; i++) {
            pages.push(
                <li key={i} className={`page-item ${currentPage === i ? 'active' : ''}`}>
                    <button
                        className="page-link"
                        onClick={() => handlePageChange(i)}
                    >
                        {i}
                    </button>
                </li>
            );
        }

        return (
            <nav aria-label="API sources pagination">
                <ul className="pagination mb-0 justify-content-end">
                    <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                        <button
                            className="page-link"
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={currentPage === 1}
                        >
                            Previous
                        </button>
                    </li>
                    {startPage > 1 && (
                        <>
                            <li className="page-item">
                                <button className="page-link" onClick={() => handlePageChange(1)}>1</button>
                            </li>
                            {startPage > 2 && (
                                <li className="page-item disabled">
                                    <span className="page-link">...</span>
                                </li>
                            )}
                        </>
                    )}
                    {pages}
                    {endPage < pagination.totalPages && (
                        <>
                            {endPage < pagination.totalPages - 1 && (
                                <li className="page-item disabled">
                                    <span className="page-link">...</span>
                                </li>
                            )}
                            <li className="page-item">
                                <button
                                    className="page-link"
                                    onClick={() => handlePageChange(pagination.totalPages)}
                                >
                                    {pagination.totalPages}
                                </button>
                            </li>
                        </>
                    )}
                    <li className={`page-item ${currentPage === pagination.totalPages ? 'disabled' : ''}`}>
                        <button
                            className="page-link"
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={currentPage === pagination.totalPages}
                        >
                            Next
                        </button>
                    </li>
                </ul>
            </nav>
        );
    };

    if (!user) {
        return (
            <PageTransition>
                <div className="container mt-5">
                    <Alert variant="warning">Please log in to view API sources.</Alert>
                </div>
            </PageTransition>
        );
    }

    return (
        <React.Fragment>
            <div className="page-container">
                <div className="mb-4">
                    <h5 className="mb-3">APIs</h5>
                    {loading ? (
                        <ContentSkeleton />
                    ) : (
                    <Card className="border-0 shadow-sm mb-4">
                        <Card.Body className="p-4">
                            {error && (
                                <Alert variant="danger" onClose={() => setError(null)} dismissible>
                                    {error}
                                </Alert>
                            )}
                            {modalState.success && (
                                <Alert variant="success" onClose={() => modalState.setSuccess(null)} dismissible>
                                    {modalState.success}
                                </Alert>
                            )}

                            <div className="d-flex justify-content-between align-items-center mb-4">
                                <div className="flex-grow-1">
                                    <div className="mt-3">
                                        <Form.Group>
                                            <div className="d-flex gap-2">
                                                <div className="position-relative flex-grow-1">
                                                    <div className="position-absolute top-50 translate-middle-y ps-3">
                                                        <Search size={16} className="text-muted" />
                                                    </div>
                                                    <Form.Control
                                                        type="text"
                                                        placeholder="Search API services..."
                                                        value={searchTerm}
                                                        onChange={(e) => handleSearch(e.target.value)}
                                                        className="ps-5 border"
                                                        style={{ height: '38px' }}
                                                    />
                                                </div>
                                                <button
                                                    className="btn btn-outline-primary d-flex align-items-center gap-2"
                                                    onClick={() => navigate('/data-sources/api/create')}
                                                    style={{height: '38px', whiteSpace: 'nowrap'}}
                                                >
                                                    <Plus size={16}/>
                                                    Add API Service
                                                </button>
                                            </div>
                                        </Form.Group>
                                    </div>
                                </div>
                            </div>

                            <div className="row">
                                <div className="col-md-8">
                                    {loading ? (
                                        <div className="text-center p-2">
                                            <div className="spinner-border text-primary" role="status">
                                                <span className="visually-hidden">Loading...</span>
                                            </div>
                                        </div>
                                    ) : sources.length === 0 ? (
                                        <div className="text-center py-4 text-muted">
                                            No API sources found.
                                        </div>
                                    ) : (
                                        <>
                                            <div className="list-group">
                                                {sources.map((source) => (
                                                    <div
                                                        key={source.id}
                                                        className={`list-group-item py-2 px-3 ${
                                                            selectedSource?.id === source.id ? 'selected' : ''
                                                        }`}
                                                        onClick={() => setSelectedSource(source.id === selectedSource?.id ? null : source)}
                                                    >
                                                        <div className="d-flex align-items-start">
                                                            <Code size={18} className="text-muted me-2 mt-1"/>
                                                            <div className="flex-grow-1">
                                                                <div className="d-flex justify-content-between align-items-center">
                                                                    <div>
                                                                        <h6 className="mb-0 text-truncate">{source.name}</h6>
                                                                        <div className="text-muted small">
                                                                            {getMethodAndEndpoint(source)}
                                                                            <span className="ms-2">
                                                                                · {getRequestsPerMinute(source)} requests/min
                                                                            </span>
                                                                        </div>
                                                                    </div>
                                                                    <OverlayTrigger
                                                                        placement="left"
                                                                        delay={{ show: 100, hide: 100 }}
                                                                        overlay={(props) =>
                                                                            source.used_by_kbs && source.used_by_kbs.length > 0 ? (
                                                                                <Tooltip id={`status-tooltip-${source.id}`} {...props}>
                                                                                    <div className="text-start">
                                                                                        <div className="mb-1">Used in Knowledge Bases:</div>
                                                                                        <ul className="list-unstyled m-0">
                                                                                            {source.used_by_kbs.map(kb => (
                                                                                                <li key={kb.id}>{kb.name}</li>
                                                                                            ))}
                                                                                        </ul>
                                                                                    </div>
                                                                                </Tooltip>
                                                                            ) : <></>
                                                                        }
                                                                    >
                                                                        <Badge
                                                                            className={`ms-2 px-2 py-1 text-xs ${
                                                                                source.used_by_kbs && source.used_by_kbs.length > 0
                                                                                    ? 'bg-success-subtle text-success-emphasis'
                                                                                    : 'bg-secondary-subtle text-secondary-emphasis'
                                                                            }`}
                                                                        >
                                                                            {source.used_by_kbs && source.used_by_kbs.length > 0 ? 'In Use' : 'Not Used'}
                                                                        </Badge>
                                                                    </OverlayTrigger>
                                                                </div>
                                                                {source.description && (
                                                                    <p className="text-muted small mb-0 mt-1">
                                                                        {source.description}
                                                                    </p>
                                                                )}
                                                                <div
                                                                    className="text-muted small mt-1"
                                                                    style={{
                                                                        textAlign: 'right'
                                                                    }}
                                                                >
                                                                    {source.created_at === source.updated_at ? (
                                                                        <>Created on {source.created_at ? new Date(source.created_at).toLocaleDateString('en-US', {
                                                                            year: 'numeric',
                                                                            month: 'short',
                                                                            day: 'numeric'
                                                                        }) : 'Unknown date'}</>
                                                                    ) : (
                                                                        <>Updated on {source.updated_at ? new Date(source.updated_at).toLocaleDateString('en-US', {
                                                                            year: 'numeric',
                                                                            month: 'short',
                                                                            day: 'numeric'
                                                                        }) : 'Unknown date'}</>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>

                                            <div className="d-flex justify-content-between align-items-center mt-5">
                                                <div className="text-muted small">
                                                    Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} entries
                                                </div>
                                                {pagination.totalPages > 0 && renderPagination()}
                                            </div>
                                        </>
                                    )}
                                </div>

                                <div className="col-md-4">
                                    <Card className="border shadow-sm">
                                        <Card.Header className="border-bottom px-3 py-2">
                                            <h6 className="mb-0">Actions</h6>
                                        </Card.Header>
                                        <Card.Body className="p-2">
                                            <div className="d-grid gap-1">
                                                <button
                                                    className="w-100 d-flex align-items-center justify-content-center gap-2 px-3 py-1 border rounded text-sm fw-medium btn btn-outline-secondary text-dark action-button"
                                                    disabled={!selectedSource}
                                                    onClick={() => selectedSource && handleTestSource(selectedSource.id)}
                                                >
                                                    <RefreshCw size={16}/>
                                                    Test Connection
                                                </button>
                                                <button
                                                    className="w-100 d-flex align-items-center justify-content-center gap-2 px-3 py-1 border rounded text-sm fw-medium btn btn-outline-secondary text-dark action-button"
                                                    disabled={!selectedSource}
                                                    onClick={() => selectedSource && navigate(`/data-sources/api/${selectedSource.id}/edit`)}
                                                >
                                                    <Edit2 size={16}/>
                                                    Edit
                                                </button>
                                                <button
                                                    className="w-100 d-flex align-items-center justify-content-center gap-2 px-3 py-1 border rounded text-sm fw-medium btn btn-outline-secondary text-dark action-button"
                                                    disabled={!selectedSource}
                                                    onClick={() => selectedSource && handleDeleteSource(selectedSource)}
                                                >
                                                    <Trash2 size={16}/>
                                                    Delete
                                                </button>
                                            </div>
                                        </Card.Body>
                                    </Card>
                                </div>
                            </div>
                        </Card.Body>
                    </Card>
                    )}
                </div>
            </div>
            <DeleteConfirmationModal
                isOpen={showDeleteModal}
                onClose={() => {
                    setShowDeleteModal(false);
                    setSourceToDelete(null);
                }}
                onConfirm={confirmDelete}
                resourceType="API Source"
                resourceName={sourceToDelete?.name || ''}
                isLoading={loading}
            />
        </React.Fragment>
    );
};

export default APISourcesIndex;